// Job Hub (George 2026-07-02) — the central drill-in for one job on the
// Operations Active Jobs feed: sales header + AM contact, the sales request
// questionnaire, the customer's site requirements, staffing + readiness,
// the equipment picture, files, and ISN. Everything read-only except Files
// (which reuses the caller's JobFilesPanel via the FilesPanel prop so upload
// + drag-out behave exactly like the Active Jobs expander).
// COST WALL: renders only what /api/operations/projects/:id/hub returns —
// that endpoint never selects cost/pay/margin/target rates.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';
import { styles, fmtMoney, fmtDate, fmtDateTime, StatusPill, useIsMobile } from './UI';
import { useJobCompliance, ComplianceChip, ComplianceModal } from './CrewCompliance';
import { HascCrewRegister } from './HascRegistration';
import QuickCrewAdd from './QuickCrewAdd';
import { siteAccessHref } from '../utils/siteAccessView';

const ORANGE = '#E36F2C';

const SECTIONS = [
  { key: 'request',  label: '📄 Request Q&A' },
  { key: 'sitereqs', label: '🛡️ Site Requirements' },
  { key: 'staffing', label: '👷 Staffing' },
  { key: 'equipment', label: '📦 Equipment' },
  { key: 'vendorpos', label: '🧾 Vendor POs' },
  { key: 'files',    label: '📎 Files' },
];

// Sections come COLLAPSED by default (George 2026-07-16) — open state is
// lifted to JobHub so the chip nav can expand the section it jumps to.
// Facility override control (Custofab-at-CHS-Laurel, 2026-07-30). When SSS
// subs for another contractor the request chain carries who we BILL, but the
// crew walks the FACILITY's gate — Site Ready, Site Access job mode and this
// hub's requirements section must judge against the facility. Setting it here
// flips all of them at once; Clear returns to the billing customer.
function FacilityPicker({ hub, projectId, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [customers, setCustomers] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const isFacility = !!hub.customer?.facility;

  const startEdit = () => {
    setEditing(true); setErr('');
    if (!customers) api.listCustomers().then(setCustomers).catch(e => setErr(e.message));
  };
  const save = async (customerId) => {
    setSaving(true); setErr('');
    try {
      await api.opsSetFacility(projectId, customerId);
      setEditing(false);
      onChanged();
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ marginBottom: 10, padding: '8px 10px', background: isFacility ? '#EFF6FF' : '#F9FAFB',
                  border: `1px solid ${isFacility ? '#BFDBFE' : '#E5E7EB'}`, borderRadius: 6, fontSize: 12 }}>
      {isFacility ? (
        <>🏭 Judged against the <strong>facility</strong>: {hub.customer.name}
          {hub.billing_customer ? <span style={{ color: '#6B7280' }}> · billing under {hub.billing_customer.name}</span> : null}</>
      ) : (
        <>Judged against the billing customer{hub.customer ? <> ({hub.customer.name})</> : null}.
          <span style={{ color: '#6B7280' }}> Working as a sub? Set the facility so Site Ready checks the right gate.</span></>
      )}
      {!editing && (
        <button style={{ ...styles.btnSecondary, fontSize: 11, padding: '2px 8px', marginLeft: 8 }}
          onClick={startEdit}>{isFacility ? 'Change' : 'Set facility'}</button>
      )}
      {isFacility && !editing && (
        <button style={{ ...styles.btnSecondary, fontSize: 11, padding: '2px 8px', marginLeft: 6 }}
          disabled={saving} onClick={() => save(null)}>Clear</button>
      )}
      {editing && (
        <span style={{ marginLeft: 8 }}>
          {!customers ? 'Loading customers…' : (
            <select style={{ ...styles.input, width: 260, display: 'inline-block', padding: '3px 6px', fontSize: 12 }}
              defaultValue="" disabled={saving}
              onChange={e => e.target.value && save(Number(e.target.value))}>
              <option value="">— pick the facility owner —</option>
              {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          )}
          <button style={{ ...styles.btnSecondary, fontSize: 11, padding: '2px 8px', marginLeft: 6 }}
            onClick={() => setEditing(false)} disabled={saving}>Cancel</button>
        </span>
      )}
      {err && <div style={{ color: '#B91C1C', marginTop: 4 }}>{err}</div>}
    </div>
  );
}

// Job site control (Site Access overhaul, decision 9, 2026-09-23), beside the
// facility override: which of the judged customer's sites this job runs at.
// That site's requirements row then governs Site Ready, Site Access, the
// dispatch email and the readiness digest; with none set (or no row for the
// site) the customer-wide row does. A site left over from a different
// facility is STALE: the server ignores it, and it is flagged here to fix.
function JobSitePicker({ hub, projectId, onChanged }) {
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const site = hub.customer_site || null;
  const sites = hub.customer_sites || [];
  if (!hub.customer?.id || (!sites.length && !site)) return null;
  const live = site && !site.stale ? site : null;
  // A linked site that is no longer active still shows as the current choice.
  const options = live && !sites.some(s => s.id === live.id) ? [...sites, { id: live.id, name: live.name }] : sites;
  const applied = hub.requirements_applied;
  const save = async (siteId) => {
    setSaving(true); setErr('');
    try {
      await api.opsSetJobSite(projectId, siteId);
      onChanged();
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ marginBottom: 10, padding: '8px 10px', background: site?.stale ? '#FFFBEB' : '#F9FAFB',
                  border: `1px solid ${site?.stale ? '#FCD34D' : '#E5E7EB'}`, borderRadius: 6, fontSize: 12 }}>
      {site?.stale ? (
        <span style={{ color: '#B45309', fontWeight: 600 }}>
          ⚠ Stale site: {site.name || `site #${site.id}`} belongs to {site.customer_name || 'another customer'}, not {hub.customer.name}, so it is ignored. Pick this job's site or clear it.
        </span>
      ) : live ? (
        <>📍 Job site: <strong>{live.name}</strong>
          <span style={{ color: '#6B7280' }}>
            {applied && applied.site_id === live.id ? ' · its site requirements apply' : ' · no requirements row for this site, the customer-wide row applies'}
          </span></>
      ) : (
        <>Job site not set: {applied ? `requirements come from ${applied.source === 'customer-wide' ? 'the customer-wide row' : applied.source}` : 'no requirements row applies'}.
          <span style={{ color: '#6B7280' }}> Pick the site so its own requirements are checked.</span></>
      )}
      <span style={{ marginLeft: 8, whiteSpace: 'nowrap' }}>
        <select style={{ ...styles.input, width: 220, display: 'inline-block', padding: '3px 6px', fontSize: 12 }}
          value={live ? String(live.id) : ''} disabled={saving}
          onChange={e => save(e.target.value ? Number(e.target.value) : null)}>
          <option value="">— no site (customer-wide) —</option>
          {options.map(s => <option key={s.id} value={String(s.id)}>{s.name}</option>)}
        </select>
        {site && (
          <button style={{ ...styles.btnSecondary, fontSize: 11, padding: '2px 8px', marginLeft: 6 }}
            disabled={saving} onClick={() => save(null)}>Clear</button>
        )}
      </span>
      {err && <div style={{ color: '#B91C1C', marginTop: 4 }}>{err}</div>}
    </div>
  );
}

function SectionCard({ id, title, count, children, innerRef, open, onToggle }) {
  return (
    <div ref={innerRef} id={id} style={{ ...styles.card, marginBottom: 12, scrollMarginTop: 90 }}>
      <div onClick={onToggle}
        style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none' }}>
        <h2 style={{ ...styles.h2, margin: 0, flex: 1 }}>
          {title}{count != null && <span style={{ color: '#6B7280', fontWeight: 400 }}> · {count}</span>}
        </h2>
        <span style={{ color: '#6B7280', fontSize: 14 }}>{open ? '▾' : '▸'}</span>
      </div>
      {open && <div style={{ marginTop: 10 }}>{children}</div>}
    </div>
  );
}

const kv = { fontSize: 11, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.04em' };
const kvVal = { fontSize: 13, fontWeight: 600, color: '#1F2937' };
const chip = (bg, fg) => ({
  display: 'inline-block', padding: '2px 10px', borderRadius: 10,
  fontSize: 11, fontWeight: 700, background: bg, color: fg, marginRight: 4, marginBottom: 4,
});
// Onsite-roster verdicts (services/onsiteRoster.js status values).
// Link control on the Onsite block (2026-09-22): "this person per Field
// Services IS this roster employee." One typeahead, Link. The server saves
// an fs_roster_links row (by FS's employee id, else the FS name as written)
// and re-judges the job on the spot, so the row flips without waiting for
// FS's next hourly push. Rows already matched get a quiet "wrong person?".
function OnsiteLink({ projectId, row, roster, loadRoster, onLinked }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const active = (roster || []).filter(e => e.is_active !== false && e.full_name);
  const emp = active.find(e => e.full_name.trim().toLowerCase() === name.trim().toLowerCase()) || null;
  const save = async () => {
    if (!emp || busy) return;
    setBusy(true); setErr('');
    try {
      await api.opsLinkOnsite(projectId, row.fs_roster_id, emp.id);
      setOpen(false); setName('');
      onLinked();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };
  if (!open) {
    return row.employee_id
      ? <button type="button" onClick={() => { setOpen(true); loadRoster(); }}
          style={{ background: 'none', border: 'none', padding: 0, marginLeft: 6, fontSize: 10, color: '#9CA3AF', cursor: 'pointer', textDecoration: 'underline' }}>wrong person?</button>
      : <button type="button" onClick={() => { setOpen(true); loadRoster(); }}
          style={{ ...styles.btn, fontSize: 11, padding: '2px 8px', marginLeft: 6 }}>Link…</button>;
  }
  return (
    <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center', flexWrap: 'wrap', marginLeft: 6 }}>
      <input list="hub-onsite-roster" autoFocus value={name}
        style={{ ...styles.input, width: 200, padding: '3px 6px', fontSize: 12 }}
        placeholder={roster ? `Roster name (${active.length} active)…` : 'Loading roster…'}
        onChange={e => setName(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); save(); } if (e.key === 'Escape') setOpen(false); }} />
      <button type="button" style={{ ...styles.btn, fontSize: 11, padding: '3px 8px' }} disabled={!emp || busy} onClick={save}>{busy ? 'Linking…' : 'Link'}</button>
      <button type="button" style={{ ...styles.btnSecondary, fontSize: 11, padding: '3px 8px' }} onClick={() => setOpen(false)}>Cancel</button>
      {err && <span style={{ fontSize: 11, color: '#B91C1C' }}>{err}</span>}
    </span>
  );
}

const ONSITE_STATUS = {
  dispatched: { bg: '#D1FAE5', fg: '#065F46' },
  added: { bg: '#DBEAFE', fg: '#1E40AF' },
  ended: { bg: '#F3F4F6', fg: '#374151' },
  unmatched: { bg: '#FEE2E2', fg: '#B91C1C' },
  ambiguous: { bg: '#FEE2E2', fg: '#B91C1C' },
  inactive_employee: { bg: '#FEF3C7', fg: '#92400E' },
  laid_off: { bg: '#FEE2E2', fg: '#B91C1C' },
};

export default function JobHub({ projectId, onClose, FilesPanel }) {
  const [hub, setHub] = useState(null);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');
  const refs = useRef({});
  const navigate = useNavigate();
  // Computed crew compliance (green/red vs site requirements). Hub crew rows
  // don't carry allocation ids, so entries key on name|craft (unique per job
  // via the project+employee+craft constraint).
  const jobComp = useJobCompliance(projectId);
  // The open modal holds the entry's ALLOCATION id and re-reads the entry
  // from the latest jobComp, so a badge added from the modal re-judges it
  // (it used to keep the pre-badge verdict over a chip that had turned green).
  const [compEntryId, setCompEntryId] = useState(null);
  const compEntry = compEntryId != null
    ? (jobComp?.crew || []).find(c => c.allocation_id === compEntryId) || null : null;
  const compByNameCraft = new Map((jobComp?.crew || []).map(c => [`${c.name}|${c.craft || ''}`, c]));
  // "+ Add crew" straight from the hub (George 2026-09-22) — the one-screen
  // QuickCrewAdd modal; the hub reloads its staffing block after each add.
  const [quickAdd, setQuickAdd] = useState(false);
  // Roster for the Onsite block's Link control — fetched once, on first use.
  const [onsiteRoster, setOnsiteRoster] = useState(null);
  const loadOnsiteRoster = () => {
    if (onsiteRoster) return;
    api.listEmployees().then(rows => setOnsiteRoster(Array.isArray(rows) ? rows : [])).catch(() => setOnsiteRoster([]));
  };
  const isMobile = useIsMobile();

  // Section open/closed state — everything starts collapsed; the chip nav
  // expands the section it jumps to (jumping to a closed card would land
  // the user on a header with nothing under it).
  const [openSections, setOpenSections] = useState({});
  const toggleSection = (key) => setOpenSections(prev => ({ ...prev, [key]: !prev[key] }));

  const reloadHub = () => api.opsJobHub(projectId).then(setHub).catch(e => setError(e.message));
  useEffect(() => {
    setHub(null); setError(''); setOpenSections({});
    reloadHub();
  }, [projectId]);

  const jump = (key) => {
    setOpenSections(prev => ({ ...prev, [key]: true }));
    // Scroll after the expanded content renders so the position is right.
    setTimeout(() => refs.current[key]?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60);
  };

  // Email-to-PM (George 2026-07-15): sends the whole hub package + the job's
  // files to the PM. Recipient pre-fills from crew allocated with a Project
  // Manager craft (there's no dedicated PM field on a job) but stays fully
  // editable. Every send is logged server-side and shown in the header.
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [emailBusy, setEmailBusy] = useState(false);
  const openEmail = () => {
    setEmailTo((hub?.pm_candidates || []).map(c => c.email).filter(Boolean).join(', '));
    setEmailOpen(true);
  };
  const sendEmail = async () => {
    setEmailBusy(true);
    try {
      // The server queues the send and answers immediately (a big package —
      // R2 pulls + SMTP upload — can take 30s+, which outlives the proxy's
      // response window). The 'sending' row shows in the header log right
      // away and flips to sent/failed when the background worker finishes.
      const r = await api.opsEmailJobHub(projectId, emailTo);
      alert(`Sending to ${r.to}${r.cc ? ` (you're CC'd at ${r.cc})` : ''} — this runs in the background.\n\nThe send log on this page flips from "sending" to "sent" when it completes (usually under a minute).`);
      setEmailOpen(false);
      api.opsJobHub(projectId).then(setHub).catch(() => {});
      // One refresh after the typical send duration to catch the status flip.
      setTimeout(() => api.opsJobHub(projectId).then(setHub).catch(() => {}), 45000);
    } catch (e) {
      alert(`Send failed: ${e.message}`);
    } finally {
      setEmailBusy(false);
    }
  };

  // Vendor PO edits save per-field on blur; PO / vendor quote # are
  // administrative (no pricing), so the server accepts them even when the
  // estimate is Approved/locked. Local state updates in place — no reload.
  const saveVendorPo = async (kind, lineId, field, raw) => {
    const value = String(raw || '').trim() || null;
    const listKey = kind === 'equipment' ? 'third_party' : 'consumables';
    const line = hub?.vendor_pos?.[listKey]?.find(l => l.id === lineId);
    if (!line || (line[field] || null) === value) return;
    try {
      await api.opsUpdateVendorPo(projectId, { kind, line_id: lineId, [field]: value });
      setHub(h => ({
        ...h,
        vendor_pos: {
          ...h.vendor_pos,
          [listKey]: h.vendor_pos[listKey].map(l => l.id === lineId ? { ...l, [field]: value } : l),
        },
      }));
    } catch (e) {
      alert(`Save failed: ${e.message}`);
    }
  };

  const poInput = (kind, l, field, width, placeholder) => (
    <input
      key={`${kind}-${l.id}-${field}-${l[field] || ''}`}
      style={{ ...styles.input, width, fontSize: 12, padding: '4px 8px' }}
      defaultValue={l[field] || ''}
      placeholder={placeholder}
      onBlur={e => saveVendorPo(kind, l.id, field, e.target.value)}
    />
  );

  const vendorPoRows = (kind, lines, describe) => lines.map(l => (
    <tr key={`${kind}${l.id}`}>
      <td style={styles.td}>{describe(l)}</td>
      <td style={styles.td}>{l.qty}{l.unit ? ` ${l.unit}` : ''}</td>
      <td style={styles.td}>{poInput(kind, l, 'vendor_name', 130, 'Vendor')}</td>
      <td style={styles.td}>{poInput(kind, l, 'po_number', 110, 'SSS PO #')}</td>
      <td style={styles.td}>{poInput(kind, l, 'vendor_quote_number', 110, 'Quote #')}</td>
      <td style={styles.td}>
        {(l.quote_files || []).length === 0 && <span style={{ color: '#9CA3AF', fontSize: 12 }}>—</span>}
        {(l.quote_files || []).map((f, i) => (
          <a key={i} href={`/api/dl/${f.dl_token}`} title="Download the vendor's quote"
            style={{ display: 'block', color: '#3730A3', fontWeight: 600, fontSize: 12, textDecoration: 'none' }}>
            📄 {f.name}
          </a>
        ))}
      </td>
    </tr>
  ));

  // Q&A grouped by section, filtered by the search box (matches question OR answer).
  const qaSections = useMemo(() => {
    if (!hub?.request?.answers) return [];
    const needle = q.trim().toLowerCase();
    const hit = (a) => !needle
      || String(a.question).toLowerCase().includes(needle)
      || String(a.response).toLowerCase().includes(needle);
    const bySection = new Map();
    for (const a of hub.request.answers.filter(hit)) {
      if (!bySection.has(a.section)) bySection.set(a.section, []);
      bySection.get(a.section).push(a);
    }
    return [...bySection.entries()];
  }, [hub, q]);

  const p = hub?.project;
  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: '#F3F4F6', borderRadius: 8, width: 980, maxWidth: '96vw', maxHeight: '92vh',
        // Desktop pins the header (title + chip nav) and scrolls the body.
        // On phones (especially zoomed) the stacked chips make that header
        // taller than the modal, starving the body of scroll room — so the
        // WHOLE modal scrolls as one unit instead ("scroll sticks", George
        // 2026-07-07).
        ...(isMobile
          ? { overflowY: 'auto', WebkitOverflowScrolling: 'touch' }
          : { display: 'flex', flexDirection: 'column', overflow: 'hidden' }),
      }}>
        {/* Header + sticky nav */}
        <div style={{ background: '#fff', borderBottom: '1px solid #E5E7EB', padding: '14px 20px 10px' }}>
          {!hub && !error && <div style={{ color: '#6B7280', fontSize: 13 }}>Loading job…</div>}
          {error && <div style={{ color: '#B91C1C', fontSize: 13 }}>⚠ {error}</div>}
          {p && (
            <>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 280 }}>
                  <div style={{ fontSize: 17, fontWeight: 700, color: '#1F2937' }}>
                    {p.site} <StatusPill status={p.status} />
                  </div>
                  <div style={{ fontSize: 12.5, color: '#6B7280', marginTop: 2 }}>
                    {p.job_number ? <strong style={{ color: ORANGE }}>{p.job_number}</strong> : 'no job # yet'}
                    {hub.customer ? <> · {hub.customer.name}</> : null}
                    {' · '}{p.branch_name || '—'}
                    {' · '}{fmtDate(p.date_planned)}{p.duration_days ? ` → ${p.duration_days} days` : ''}
                    {p.po_number ? <> · PO {p.po_number}</> : null}
                    {/* Estimate and Actual side by side, same as the Email-to-PM
                        line: actual || estimate showed a $1.48M job as its
                        first RentalMan invoice (George 2026-09-14). */}
                    {' · Est. '}{fmtMoney(p.estimated_value)}
                    {p.actual_value != null && <>{' · Actual '}{fmtMoney(p.actual_value)}</>}
                  </div>
                  <div style={{ fontSize: 12.5, color: '#374151', marginTop: 4 }}>
                    <strong>AM:</strong> {p.account_owner_name || '—'}
                    {p.account_owner_email && <> · <a href={`mailto:${p.account_owner_email}`} style={{ color: ORANGE }}>{p.account_owner_email}</a></>}
                    {p.account_owner_phone && <> · {p.account_owner_phone}</>}
                  </div>
                  {(hub.email_log || []).length > 0 && (
                    <div style={{ fontSize: 11.5, color: '#6B7280', marginTop: 4 }}>
                      {hub.email_log.slice(0, 3).map((l, i) => (
                        <div key={i} style={l.status === 'failed' ? { color: '#B91C1C', fontWeight: 600 } : l.status === 'sending' ? { color: '#B45309' } : {}}>
                          {l.status === 'failed' ? '✗ Send FAILED to' : l.status === 'sending' ? '⏳ Sending to' : '📧 Sent to'}{' '}
                          <strong>{l.sent_to}</strong> by {l.sent_by_name || '—'} · {fmtDateTime(l.sent_at)}
                          {l.status !== 'sending' && l.files_attached > 0 && <> · {l.files_attached} file{l.files_attached === 1 ? '' : 's'}</>}
                        </div>
                      ))}
                      {hub.email_log.length > 3 && <div>…and {hub.email_log.length - 3} earlier send{hub.email_log.length - 3 === 1 ? '' : 's'}</div>}
                    </div>
                  )}
                </div>
                <button onClick={onClose} style={{
                  border: 'none', background: 'none', fontSize: 20, cursor: 'pointer', color: '#6B7280',
                }}>✕</button>
              </div>
              <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                {SECTIONS.map(s => (
                  <button key={s.key} onClick={() => jump(s.key)} style={{
                    padding: '5px 12px', borderRadius: 14, fontSize: 12, fontWeight: 600,
                    border: '1px solid #C3C8D0', background: 'linear-gradient(#FFFFFF, #F3F4F6)', color: '#374151', cursor: 'pointer',
                    boxShadow: '0 1px 2px rgba(17, 24, 39, 0.12), 0 2px 4px rgba(17, 24, 39, 0.06)',
                  }}>{s.label}</button>
                ))}
                <button onClick={openEmail} title="Email everything on this page + the job's files to the PM" style={{
                  padding: '5px 12px', borderRadius: 14, fontSize: 12, fontWeight: 700,
                  border: `1px solid ${ORANGE}`, background: 'linear-gradient(#FFFFFF, #FFF7ED)', color: ORANGE, cursor: 'pointer',
                  boxShadow: '0 1px 2px rgba(17, 24, 39, 0.12), 0 2px 4px rgba(17, 24, 39, 0.06)',
                }}>📧 Email to PM</button>
                <span style={{ flex: 1 }} />
                <input
                  style={{ ...styles.input, width: 220, fontSize: 12, padding: '6px 10px' }}
                  placeholder="🔍 Search the questionnaire…"
                  value={q}
                  onChange={e => setQ(e.target.value)}
                />
              </div>
            </>
          )}
        </div>

        {/* Scrollable body (desktop only — on mobile the whole modal scrolls) */}
        {p && (
          <div style={{ padding: '14px 20px', ...(isMobile ? {} : { overflowY: 'auto' }) }}>

            <SectionCard id="hub-request" innerRef={el => (refs.current.request = el)}
              open={!!openSections.request} onToggle={() => toggleSection('request')}
              title={`📄 Sales Request ${hub.request ? `— ${hub.request.label}` : ''}`}
              count={hub.request ? hub.request.answers.length : 0}>
              {!hub.request && <div style={{ color: '#9CA3AF', fontSize: 13 }}>No sales request is linked to this job.</div>}
              {hub.request && (
                <>
                  {hub.request.scope_labels.length > 0 && (
                    <div style={{ marginBottom: 10 }}>
                      {hub.request.scope_labels.map(l => <span key={l} style={chip('#1F2937', '#fff')}>{l}</span>)}
                    </div>
                  )}
                  {/* CSVM vendor response — SSS PO# + the vendor's quote,
                      recorded on the request by sales/leadership. Read-only;
                      the download rides the tokenized /api/dl route. */}
                  {hub.request.csvm && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
                      background: '#EEF2FF', border: '1px solid #A5B4FC', borderRadius: 6,
                      padding: '8px 12px', marginBottom: 10, fontSize: 13 }}>
                      <span style={{ fontWeight: 700, color: '#3730A3', fontSize: 12 }}>CSVM Vendor</span>
                      <span><span style={{ color: '#6B7280' }}>SSS PO #:</span>{' '}
                        <strong>{hub.request.csvm.po_number || '—'}</strong></span>
                      <span><span style={{ color: '#6B7280' }}>Vendor Quote #:</span>{' '}
                        <strong>{hub.request.csvm.vendor_quote_number || '—'}</strong></span>
                      {hub.request.csvm.quote_dl_token ? (
                        <a href={`/api/dl/${hub.request.csvm.quote_dl_token}`}
                          style={{ color: '#3730A3', fontWeight: 600, textDecoration: 'none' }}
                          title="Download the CSVM vendor's quote">
                          📄 {hub.request.csvm.quote_name || 'vendor quote'}
                        </a>
                      ) : (
                        <span style={{ color: '#9CA3AF' }}>no vendor quote on file</span>
                      )}
                    </div>
                  )}
                  {qaSections.length === 0 && (
                    <div style={{ color: '#9CA3AF', fontSize: 13 }}>
                      {q ? `No questions match "${q}".` : 'No answered questions on the request.'}
                    </div>
                  )}
                  {qaSections.map(([section, items]) => (
                    <div key={section} style={{ marginBottom: 12 }}>
                      <div style={{ fontSize: 11, fontWeight: 800, color: ORANGE, textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: '1px solid #FDE8D8', paddingBottom: 3, marginBottom: 6 }}>
                        {section}
                      </div>
                      {items.map(a => (
                        <div key={a.question_n} style={{ display: 'flex', gap: 12, padding: '5px 0', borderBottom: '1px solid #F3F4F6', fontSize: 13 }}>
                          <div style={{ flex: '0 0 46%', color: '#6B7280' }}>{a.question}</div>
                          <div style={{ flex: 1, fontWeight: 600, color: '#1F2937', whiteSpace: 'pre-wrap' }}>
                            {a.response}
                            {a.estimate_override && (
                              <div style={{ color: '#B45309', fontWeight: 700, fontSize: 12, marginTop: 2 }}
                                title="The sales request's answer disagrees with the current estimate's labor schedule — the estimate governs dates.">
                                ⚠ Per estimate{hub.estimate_window ? ` ${hub.estimate_window.label}` : ''}: {a.estimate_override} — the estimate governs.
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                </>
              )}
            </SectionCard>

            <SectionCard id="hub-sitereqs" innerRef={el => (refs.current.sitereqs = el)}
              open={!!openSections.sitereqs} onToggle={() => toggleSection('sitereqs')}
              title={`🛡️ Site Requirements ${hub.customer ? `— ${hub.customer.name}${hub.customer.facility ? ' (facility)' : ''}` : ''}`}
              count={hub.site_requirements.length}>
              {/* Facility override (Custofab-at-CHS-Laurel, 2026-07-30): when
                  SSS subs for another contractor, compliance must run against
                  the FACILITY's requirements, not who we bill. */}
              <FacilityPicker hub={hub} projectId={projectId} onChanged={() => { reloadHub(); jobComp.reload && jobComp.reload(); }} />
              <JobSitePicker hub={hub} projectId={projectId} onChanged={() => { reloadHub(); jobComp.reload && jobComp.reload(); }} />
              {!hub.customer &&<div style={{ color: '#9CA3AF', fontSize: 13 }}>No customer resolved for this job (link a sales request with a customer).</div>}
              {hub.customer && hub.site_requirements.length === 0 && (
                <div style={{ color: '#9CA3AF', fontSize: 13 }}>No requirements on file for this customer yet.</div>
              )}
              {hub.site_requirements.map((r, i) => (
                <div key={i} style={{ marginBottom: 12, paddingBottom: 10, borderBottom: '1px solid #F3F4F6' }}>
                  <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6 }}>
                    {r.baseline ? 'SSS baseline (nothing saved on this customer’s 📋 Reqs yet)'
                      : r.site_name ? `Site: ${r.site_name}` : `Customer-wide${hub.customer.cascade ? ' (applies to every site without its own override)' : ''}`}
                    {hub.requirements_applied && (hub.requirements_applied.site_id ?? null) === (r.site_id ?? null) && (
                      <span style={{ ...chip('#DCFCE7', '#166534'), marginLeft: 8, fontSize: 11 }}
                        title="Site Ready, Site Access and the dispatch email judge this job's crew on this row.">
                        ✓ Applies to this job
                      </span>
                    )}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10, fontSize: 13 }}>
                    <div>
                      <div style={kv}>DISA / Background</div>
                      <div style={kvVal}>
                        {r.disa_required ? (r.disa_tests || []).join(', ') || 'Required' : 'Not required'}
                        {r.disa_max_age_days ? <div style={{ fontWeight: 400, color: '#6B7280' }}>Drug test within {r.disa_max_age_days} days</div> : null}
                      </div>
                    </div>
                    <div>
                      <div style={kv}>Training / Certs</div>
                      <div style={kvVal}>{(r.training_required_certs || []).join(', ') || '—'}</div>
                    </div>
                    <div>
                      <div style={kv}>Safety Council</div>
                      <div style={kvVal}>
                        {r.safety_council_required
                          ? (r.safety_council_classes || []).map((c2, j) =>
                              `${c2}${(r.safety_council_class_codes || [])[j] ? ` (${r.safety_council_class_codes[j]})` : ''}`).join(', ') || 'Required'
                          : 'Not required'}
                        {r.safety_council_facility ? <div style={{ fontWeight: 400, color: '#6B7280' }}>Facility: {r.safety_council_facility}</div> : null}
                      </div>
                    </div>
                    <div>
                      <div style={kv}>Per Diem</div>
                      <div style={kvVal}>
                        {(r.per_diem_min_miles != null || r.per_diem_max_miles != null)
                          ? `${r.per_diem_min_miles ?? 0}–${r.per_diem_max_miles ?? '∞'} miles`
                          : '—'}
                        {(r.per_diem_supervision_rate != null || r.per_diem_technician_rate != null) && (
                          <div style={{ fontWeight: 400, color: '#6B7280' }}>
                            Bill/day: sup {r.per_diem_supervision_rate != null ? fmtMoney(r.per_diem_supervision_rate) : '—'} · tech {r.per_diem_technician_rate != null ? fmtMoney(r.per_diem_technician_rate) : '—'}
                          </div>
                        )}
                      </div>
                    </div>
                    <div>
                      <div style={kv}>Badging</div>
                      <div style={kvVal}>
                        {r.badging_email ? <a href={`mailto:${r.badging_email}`} style={{ color: ORANGE }}>{r.badging_email}</a> : '—'}
                        {(r.required_badging_docs || []).length > 0 && (
                          <div style={{ fontWeight: 400, color: '#6B7280' }}>Docs: {(r.required_badging_docs || []).join(', ')}</div>
                        )}
                        {r.badging_notes ? <div style={{ fontWeight: 400, color: '#6B7280', whiteSpace: 'pre-wrap' }}>{r.badging_notes}</div> : null}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              {hub.customer?.id && (
                <button style={{ ...styles.btnSecondary, fontSize: 12 }}
                  onClick={() => navigate(`/customers/${hub.customer.id}`)}>
                  Open full requirements + badging documents →
                </button>
              )}
              {hub.isn && (
                <div style={{ marginTop: 10, padding: '8px 12px', background: '#EFF6FF', border: '1px solid #93C5FD', borderRadius: 6, fontSize: 13 }}>
                  🌐 <strong>ISNetworld:</strong> {hub.isn.client_name} — {hub.isn.connection_status || '—'}
                  {hub.isn.grade ? ` · grade ${hub.isn.grade}` : ''}
                  {hub.isn.ravs_status ? ` · RAVS ${hub.isn.ravs_status}` : ''}
                  {hub.isn.expires_on ? ` · expires ${fmtDate(hub.isn.expires_on)}` : ''}
                </div>
              )}
            </SectionCard>

            <SectionCard id="hub-staffing" innerRef={el => (refs.current.staffing = el)}
              open={!!openSections.staffing} onToggle={() => toggleSection('staffing')}
              title="👷 Staffing & Readiness" count={hub.staffing.crew.length}>
              {hub.staffing.plan.length > 0 && (
                <div style={{ marginBottom: 10 }}>
                  {hub.staffing.plan.map(r => {
                    const short = r.assigned < r.needed;
                    return (
                      <span key={r.craft} style={chip(short ? '#FEE2E2' : '#D1FAE5', short ? '#B91C1C' : '#065F46')}
                        title={hub.onsite && hub.onsite.reported ? 'dispatched / planned · onsite per Field Services' : 'dispatched / planned'}>
                        {r.craft}: {r.assigned}/{r.needed}{hub.onsite && hub.onsite.reported ? ` · onsite ${r.onsite || 0}` : ''}
                      </span>
                    );
                  })}
                  {hub.staffing.beyond_plan > 0 && (
                    <span style={chip('#E0E7FF', '#3730A3')}
                      title="Crew on this job the staffing plan does not account for (a craft the plan does not carry, or more than it needs)">
                      +{hub.staffing.beyond_plan} beyond plan
                    </span>
                  )}
                </div>
              )}
              {hub.staffing.pending_verifications > 0 && (
                <div style={{ marginBottom: 8, fontSize: 13, fontWeight: 700, color: '#B91C1C' }}>
                  ⚠ {hub.staffing.pending_verifications} verification box{hub.staffing.pending_verifications === 1 ? '' : 'es'} still Pending
                </div>
              )}
              {hub.staffing.crew.length === 0 && <div style={{ color: '#9CA3AF', fontSize: 13 }}>No crew assigned yet.</div>}
              {hub.staffing.crew.length > 0 && (
                <table style={styles.table}>
                  <thead><tr>
                    <th style={styles.th}>Employee</th>
                    <th style={styles.th} title="Computed vs the customer's site requirements — click for reasons">Site Ready</th>
                    <th style={styles.th}>Craft</th>
                    <th style={styles.th}>Shift</th>
                    <th style={styles.th}>Email</th><th style={styles.th}>Phone</th>
                    <th style={styles.th}>Start</th>
                    <th style={styles.th}>End</th><th style={styles.th}>Pending verif.</th>
                  </tr></thead>
                  <tbody>
                    {hub.staffing.crew.map((c, i) => {
                      const comp = compByNameCraft.get(`${c.name}|${c.craft || ''}`);
                      return (
                      <tr key={i}>
                        <td style={styles.td}>{c.name}</td>
                        <td style={styles.td}>
                          <ComplianceChip entry={comp} onClick={() => comp && setCompEntryId(comp.allocation_id)} />
                        </td>
                        <td style={styles.td}>{c.craft}</td>
                        <td style={styles.td}>{c.shift || '—'}</td>
                        {/* Contact detail — same fields the emailed package carries,
                            so the page and the email can't drift. */}
                        <td style={styles.td}>
                          {c.email
                            ? <a href={`mailto:${c.email}`}>{c.email}</a>
                            : <span style={{ color: '#B45309' }}>— not on roster</span>}
                        </td>
                        <td style={{ ...styles.td, whiteSpace: 'nowrap' }}>
                          {c.phone
                            ? <a href={`tel:${c.phone}`}>{c.phone}</a>
                            : <span style={{ color: '#B45309' }}>— not on roster</span>}
                        </td>
                        <td style={styles.td}>{fmtDate(c.start_date)}</td>
                        <td style={styles.td}>{fmtDate(c.end_date)}</td>
                        <td style={{ ...styles.td, fontWeight: 700, color: c.pending ? '#B91C1C' : '#065F46' }}>
                          {c.pending || '✓'}
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
              {/* Onsite per Field Services (2026-09-22): the crew roster the PM
                  keeps on the FS job, mirrored on every FS roster write. Planned
                  = estimate, Dispatched = Allocations, Onsite = this. People FS
                  shows onsite are auto-added to Allocations; what is left here
                  is what a human still has to fix. */}
              {hub.onsite && (
                <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid #E5E7EB' }}>
                  <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 6 }}>
                    🛠 Onsite per Field Services
                    {hub.onsite.reported
                      ? <span style={{ fontWeight: 400, color: '#6B7280' }}> · as of {fmtDateTime(hub.onsite.as_of)} · {hub.onsite.rows.filter(r => r.onsite_now).length} onsite now</span>
                      : <span style={{ fontWeight: 400, color: '#B45309' }}> · Field Services has not reported a crew roster for this job yet</span>}
                  </div>
                  {hub.onsite.reported && hub.onsite.unmatched.length > 0 && (
                    <div style={{ fontSize: 12, color: '#B91C1C', marginBottom: 4 }}>
                      ⚠ Onsite but not linked to anyone on the CRM roster (use Link on the row, or put the employee # / email on the Roster tab): {hub.onsite.unmatched.join(', ')}
                    </div>
                  )}
                  {hub.onsite.reported && hub.onsite.inactive.length > 0 && (
                    <div style={{ fontSize: 12, color: '#92400E', marginBottom: 4 }}>
                      ⚠ Onsite per FS but INACTIVE on the CRM roster: {hub.onsite.inactive.join(', ')}
                    </div>
                  )}
                  {hub.onsite.reported && hub.onsite.laid_off.length > 0 && (
                    <div style={{ fontSize: 12, color: '#B91C1C', marginBottom: 4 }}>
                      ⚠ Onsite per FS but LAID OFF from this job in the CRM: {hub.onsite.laid_off.join(', ')}
                    </div>
                  )}
                  {hub.onsite.reported && hub.onsite.not_onsite.length > 0 && (
                    <div style={{ fontSize: 12, color: '#92400E', marginBottom: 4 }}>
                      ⚠ Dispatched here but not on the FS crew roster: {hub.onsite.not_onsite.map(n => `${n.name} (${n.craft})`).join(', ')}
                    </div>
                  )}
                  {hub.onsite.reported && hub.onsite.rows.length > 0 && (
                    <table style={styles.table}>
                      <thead><tr>
                        <th style={styles.th}>Onsite person</th>
                        <th style={styles.th}>FS position</th>
                        <th style={styles.th}>Shift</th>
                        <th style={styles.th}>Start</th>
                        <th style={styles.th}>End</th>
                        <th style={styles.th}>In the CRM</th>
                      </tr></thead>
                      <tbody>
                        {hub.onsite.rows.map(r => (
                          <tr key={r.fs_roster_id} style={r.onsite_now ? {} : { color: '#9CA3AF' }}>
                            <td style={styles.td}>
                              {r.name}
                              {r.employee_no ? <span style={{ color: '#6B7280', fontSize: 11 }}> #{r.employee_no}</span> : null}
                              {r.crm_name && r.crm_name !== r.name ? <span style={{ color: '#6B7280', fontSize: 11 }}> (CRM: {r.crm_name}{r.match_method === 'link' ? ' · linked' : ''})</span> : null}
                            </td>
                            <td style={styles.td}>{r.position || '—'}</td>
                            <td style={styles.td}>{r.shift || '—'}</td>
                            <td style={styles.td}>{fmtDate(r.start_date)}</td>
                            <td style={styles.td}>{fmtDate(r.end_date)}</td>
                            <td style={styles.td}>
                              <span style={chip((ONSITE_STATUS[r.status] || {}).bg || '#F3F4F6', (ONSITE_STATUS[r.status] || {}).fg || '#374151')}>
                                {r.status_label || r.status}{r.status === 'dispatched' || r.status === 'added' ? ` · ${r.craft}` : ''}
                              </span>
                              <OnsiteLink projectId={projectId} row={r} roster={onsiteRoster} loadRoster={loadOnsiteRoster} onLinked={reloadHub} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                  {onsiteRoster && (
                    <datalist id="hub-onsite-roster">
                      {onsiteRoster.filter(e => e.is_active !== false && e.full_name).map(e => <option key={e.id} value={e.full_name} />)}
                    </datalist>
                  )}
                </div>
              )}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                <button style={{ ...styles.btn, fontSize: 12 }} onClick={() => setQuickAdd(true)}
                  title="Add a person to this job on one screen — fills an open estimate slot (updates the plan) or adds a plain assignment">
                  + Add crew
                </button>
                <button style={{ ...styles.btnSecondary, fontSize: 12 }}
                  onClick={() => { onClose(); navigate(`/operations?tab=alloc&job=${projectId}`); }}
                  title="Opens this job's drill-in on the Allocations tab (dates, per diem, travel, verifications, layoffs)">
                  Manage staffing on Allocations →
                </button>
                {hub.customer?.id && (
                  // The job's own site rides along (blank = customer-wide or
                  // a stale site), the same row its Site Ready chips use.
                  <button style={{ ...styles.btnSecondary, fontSize: 12 }}
                    onClick={() => {
                      const cs = hub.customer_site;
                      onClose();
                      navigate(siteAccessHref({ customer: hub.customer.id, job: projectId, site: cs && !cs.stale ? cs.id : '' }));
                    }}
                    title="This crew on Operations → Site Access: what each person is missing, top blockers, CSV">
                    🔑 Open in Site Access →
                  </button>
                )}
              </div>
              {/* HASC Phase 2 (2026-09-23): "Register missing classes (N)" for
                  this crew, one reviewed batch. Hidden unless a HASC account is
                  configured and this user may register; the plan reloads with
                  the Site Ready data. */}
              <HascCrewRegister projectId={projectId} refreshKey={jobComp} onDone={() => jobComp?.reload?.()} />
            </SectionCard>

            <SectionCard id="hub-equipment" innerRef={el => (refs.current.equipment = el)}
              open={!!openSections.equipment} onToggle={() => toggleSection('equipment')}
              title={`📦 Equipment ${hub.equipment.estimate ? `— from ${hub.equipment.estimate.label}` : ''}`}
              count={hub.equipment.lines.length + hub.equipment.manual_lines.length}>
              {hub.equipment.lines.length === 0 && hub.equipment.manual_lines.length === 0 && (
                <div style={{ color: '#9CA3AF', fontSize: 13 }}>No equipment lines on this job.</div>
              )}
              {(hub.equipment.lines.length > 0 || hub.equipment.manual_lines.length > 0) && (
                <table style={styles.table}>
                  <thead><tr>
                    <th style={styles.th}>Description</th><th style={styles.th}>CAT-CLASS</th>
                    <th style={styles.th}>Type</th><th style={styles.th}>Qty</th>
                    <th style={styles.th}>Window</th>
                  </tr></thead>
                  <tbody>
                    {hub.equipment.lines.map((l, i) => (
                      <tr key={`e${i}`}>
                        <td style={styles.td}>{l.description}</td>
                        <td style={styles.td}>{l.cat_class || <span style={{ color: '#B45309' }} title="No CAT-CLASS — can't match Rental Man stock">⚠ none</span>}</td>
                        <td style={styles.td}>{l.equip_type}</td>
                        <td style={styles.td}>{l.qty}</td>
                        <td style={styles.td}>
                          {l.start_date ? `${fmtDate(l.start_date)} → ${fmtDate(l.end_date)}` : (l.num_days ? `${l.num_days} days` : 'labor window')}
                        </td>
                      </tr>
                    ))}
                    {hub.equipment.manual_lines.map((l, i) => (
                      <tr key={`m${i}`}>
                        <td style={styles.td}>{l.description} <span style={{ fontSize: 10, color: '#6B7280' }}>(ops-added)</span></td>
                        <td style={styles.td}>{l.cat_class || '—'}</td>
                        <td style={styles.td}>—</td>
                        <td style={styles.td}>{l.qty}</td>
                        <td style={styles.td}>{l.start_date ? `${fmtDate(l.start_date)} → ${fmtDate(l.end_date)}` : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8, flexWrap: 'wrap' }}>
                {hub.equipment.last_push && (
                  <span style={{ fontSize: 12, color: '#6B7280' }}>
                    Inventory: last {hub.equipment.last_push.kind === 'reservation' ? 'reservation' : 'pull-list push'} rev {hub.equipment.last_push.push_seq} · {hub.equipment.last_push.status} · {fmtDate(hub.equipment.last_push.created_at)}
                  </span>
                )}
                {!hub.equipment.last_push && <span style={{ fontSize: 12, color: '#9CA3AF' }}>Not pushed to Inventory yet.</span>}
                <button style={{ ...styles.btnSecondary, fontSize: 12 }}
                  onClick={() => { onClose(); navigate('/operations?tab=gantt'); }}>
                  Open Equipment Gantt →
                </button>
              </div>
            </SectionCard>

            <SectionCard id="hub-vendorpos" innerRef={el => (refs.current.vendorpos = el)}
              open={!!openSections.vendorpos} onToggle={() => toggleSection('vendorpos')}
              title={`🧾 Vendor POs ${hub.vendor_pos ? `— from ${hub.vendor_pos.estimate_label}` : ''}`}
              count={hub.vendor_pos ? hub.vendor_pos.third_party.length + hub.vendor_pos.consumables.length : 0}>
              {!hub.vendor_pos && (
                <div style={{ color: '#9CA3AF', fontSize: 13 }}>No estimate on this job — vendor POs live on the estimate's third-party and mobilization lines.</div>
              )}
              {hub.vendor_pos && hub.vendor_pos.third_party.length === 0 && hub.vendor_pos.consumables.length === 0 && (
                <div style={{ color: '#9CA3AF', fontSize: 13 }}>No third-party rental or mobilization lines on the estimate.</div>
              )}
              {hub.vendor_pos && (hub.vendor_pos.third_party.length > 0 || hub.vendor_pos.consumables.length > 0) && (
                <>
                  <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 8 }}>
                    Record the SSS PO # and the vendor's quote # per line — saves on click-away. These are
                    administrative fields; they work even after the estimate is approved and locked.
                  </div>
                  <table style={styles.table}>
                    <thead><tr>
                      <th style={styles.th}>Line</th><th style={styles.th}>Qty</th>
                      <th style={styles.th}>Vendor</th><th style={styles.th}>SSS PO #</th>
                      <th style={styles.th}>Vendor Quote #</th><th style={styles.th}>Vendor Quote</th>
                    </tr></thead>
                    <tbody>
                      {vendorPoRows('equipment', hub.vendor_pos.third_party, l => (
                        <>
                          {l.description} <span style={chip('#FEF3C7', '#92400E')}>Third-Party</span>
                          {l.cat_class && <div style={{ fontSize: 11, color: '#6B7280' }}>{l.cat_class}</div>}
                        </>
                      ))}
                      {vendorPoRows('consumable', hub.vendor_pos.consumables, l => (
                        <>
                          {l.description} <span style={chip('#E0E7FF', '#3730A3')}>{l.category || 'Consumable'}</span>
                          {l.part_number && <div style={{ fontSize: 11, color: '#6B7280' }}>Part # {l.part_number}</div>}
                        </>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
            </SectionCard>

            <SectionCard id="hub-files" innerRef={el => (refs.current.files = el)} title="📎 Files"
              open={!!openSections.files} onToggle={() => toggleSection('files')}>
              <FilesPanel projectId={projectId} />
            </SectionCard>
          </div>
        )}
        {compEntry && <ComplianceModal entry={compEntry} job={jobComp} onClose={() => setCompEntryId(null)} onChanged={() => jobComp?.reload?.()} />}
        {quickAdd && p && (
          <QuickCrewAdd projectId={projectId} job={p} onClose={() => setQuickAdd(false)} onSaved={reloadHub} />
        )}
        {emailOpen && (
          <div onClick={() => !emailBusy && setEmailOpen(false)} style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1100,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
          }}>
            <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 8, padding: 20, width: 480, maxWidth: '94vw' }}>
              <h3 style={{ margin: '0 0 6px', fontSize: 16, color: '#1F2937' }}>📧 Email this job package to the PM</h3>
              <p style={{ fontSize: 12, color: '#6B7280', margin: '0 0 10px' }}>
                Sends everything on this page — request Q&A, site requirements, staffing, equipment,
                vendor POs — plus the job's files (attached up to 15 MB total; anything larger is
                listed with a download-in-CRM note). Lauren, George, the job's AM, and you are CC'd
                automatically so the whole team is on one email, and the send is logged on this page.
              </p>
              {(hub?.pm_candidates || []).length > 0 ? (
                <p style={{ fontSize: 12, margin: '0 0 6px' }}>
                  <strong>PM on staffing:</strong>{' '}
                  {hub.pm_candidates.map(c => `${c.full_name}${c.email ? '' : ' (no email on roster)'}`).join(', ')}
                </p>
              ) : (
                <p style={{ fontSize: 12, color: '#B45309', margin: '0 0 6px' }}>
                  No Project Manager on the staffing list yet — enter the recipient below.
                </p>
              )}
              <label style={{ fontSize: 11, color: '#6B7280', textTransform: 'uppercase', fontWeight: 700 }}>To (comma-separated)</label>
              <input
                style={{ ...styles.input, width: '100%', marginTop: 3 }}
                value={emailTo}
                onChange={e => setEmailTo(e.target.value)}
                placeholder="pm@selectsafetyusa.com"
              />
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
                <button style={styles.btnSecondary} disabled={emailBusy} onClick={() => setEmailOpen(false)}>Cancel</button>
                <button style={styles.btn} disabled={emailBusy || !emailTo.trim()} onClick={sendEmail}>
                  {emailBusy ? 'Sending…' : 'Send'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
